import { FunctionComponent, useState } from 'react';
import { Button, Card, Chip, ChipProps } from '@mui/material';
import { TicketDrawerBlock } from '../../ui/ticketDrawerContent/ticketDrawerBlock';
import { TicketDrawerLabel } from '../../ui/ticketDrawerContent/ticketDrawerLabel';
import { drawerValueToString } from '../../lib/ticketUiUtils';
import { FlexContainer } from '@starjack/shared-components';
import { InsuranceStatus, Ticket } from '../../model/ticket.types';
import { InsuranceCancelDialog } from '../../ui/ticketDrawerContent/insuranceCancelDialog';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

type InsuranceCardProps = {
    ticket: Ticket;
};

export const InsuranceCard: FunctionComponent<InsuranceCardProps> = ({
    ticket,
}) => {
    const { t, i18n } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState<
        'cancel' | 'refund' | 'cancelFromInsurance'
    >('cancel');

    const insuranceFields = [
        [`tariffName.${i18n.language}`, t('insuranceCard.product')],
        ['bookedAt', t('insuranceCard.bookedAt')],
        ['status', t('insuranceCard.status')],
        ['revocable', t('insuranceCard.revocable')],
        ['cancelable', t('insuranceCard.cancelable')],
        ['canceledAt', t('insuranceCard.canceledAt')],
        ['revokedAt', t('insuranceCard.revokedAt')],
    ];

    return (
        <>
            <Card>
                {insuranceFields.map(([key, value]) => (
                    <TicketDrawerBlock header={value} key={key}>
                        {key === 'status' ? (
                            <Chip
                                label={getInsuranceStatusLabel(
                                    ticket.insurance.status
                                )}
                                color={getInsuranceStatusColor(
                                    ticket.insurance.status
                                )}
                            />
                        ) : (
                            <TicketDrawerLabel>
                                {drawerValueToString(
                                    get(ticket?.insurance, key, '-')
                                )}
                            </TicketDrawerLabel>
                        )}
                    </TicketDrawerBlock>
                ))}

                <TicketDrawerBlock header={t('insuranceCard.action')}>
                    <FlexContainer
                        sx={{
                            justifyContent: 'space-between',
                        }}
                    >
                        <Button
                            disabled={!ticket.insurance.cancelable}
                            onClick={() => {
                                setDialogType('cancel');
                                setIsDialogOpen(true);
                            }}
                            variant='contained'
                            color='error'
                        >
                            {t('insuranceCard.cancelButton')}
                        </Button>

                        <Button
                            disabled={!ticket.insurance.revocable}
                            onClick={() => {
                                setDialogType('refund');
                                setIsDialogOpen(true);
                            }}
                            variant='contained'
                            color='error'
                        >
                            {t('insuranceCard.revokeButton')}
                        </Button>
                        <Button
                            onClick={() => {
                                setDialogType('cancelFromInsurance');
                                setIsDialogOpen(true);
                            }}
                            disabled={
                                !!ticket.insurance.canceledAt ||
                                !!ticket.insurance.revokedAt
                            }
                            variant='contained'
                            color='warning'
                        >
                            {t('insuranceCard.cancelFromInsuranceButton')}
                        </Button>
                    </FlexContainer>
                </TicketDrawerBlock>
            </Card>

            <InsuranceCancelDialog
                open={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                }}
                ticket={ticket}
                type={dialogType}
            />
        </>
    );
};

const getInsuranceStatusColor = (
    status: InsuranceStatus
): ChipProps['color'] => {
    switch (status) {
        case 'CREATED':
            return 'info';
        case 'PAID':
            return 'success';
        case 'PERMISSION_BLOCKED':
            return 'warning';
        case 'BOOKED':
            return 'success';
        case 'CANCELED':
            return 'error';
        case 'REVOKED':
            return 'error';
        default:
            return 'default';
    }
};

const getInsuranceStatusLabel = (status: InsuranceStatus) => {
    switch (status) {
        case 'CREATED':
            return 'Erstellt';
        case 'PAID':
            return 'Bezahlt';
        case 'PERMISSION_BLOCKED':
            return 'Erlaubnis blockiert';
        case 'BOOKED':
            return 'Gebucht';
        case 'CANCELED':
            return 'Storniert';
        case 'REVOKED':
            return 'Widerrufen';
        default:
            return '-';
    }
};
